// PromptTemplateBase.js
import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { debounce, getTeamIdFromURL } from '../../_helpers';
import { getPromptTemplates } from '@/_services/api/promptTemplate';
import PromptTemplateTableContainer from './PromptTemplateTableContainer';
import PromptTemplateFilters from './PromptTemplateFilters';
import NavigationBar from '../Admin/reusableComponents/NavigationBar';
import WelcomeUser from '../Admin/reusableComponents/WelcomeUser';

export const PromptTemplateBase = ({ isAdmin = false }) => {
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [searchType, setSearchType] = useState(null);
    const teamId = isAdmin ? undefined : getTeamIdFromURL();
    const inputRef = useRef();

    const onPageChanged = ({ currentPage } = {}) => {
        setCurrentPage(Math.max(currentPage || 1, 1));
    };

    const onSearchTypeChange = (value) => {
        setSearchType(value);
    };

    const loadPromptTemplates = async ({ page = 1, searchText, searchType } = {}) => {
        try {
            setLoading(true);
            const params = {
                archived: !isActive,
                page,
                searchText,
                searchType,
            };
            if (searchType) {
                params.searchType = searchType?.value;
            }
            if (!isAdmin && teamId) {
                params.teamId = teamId;
            }

            const response = await getPromptTemplates(params);
            setData(response);
        } catch (error) {
            if (error.name === 'AbortError') return;
            toast.error('Error loading prompt templates');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePromptTemplate = (updatedPromptObj) => {
        setData((prev) => ({
            ...prev,
            results: (prev.results || []).map((template) => {
                if (template && template?._id && template?._id === updatedPromptObj?._id) {
                    return {
                        ...template,
                        ...updatedPromptObj,
                    };
                }
                return template;
            }),
        }));
    };

    const fetchOnAction = (page = 1) => {
        if (page === 1) {
            loadPromptTemplates({ page: 1 });
        } else {
            setCurrentPage(page);
        }
    };

    const onSearch = debounce((val) => {
        setSearchText(val.trimLeft());
    }, 1000);

    useEffect(() => {
        loadPromptTemplates({ page: currentPage, searchText, searchType });
    }, [isActive, currentPage, searchText, searchType]);

    return (
        <div className={isAdmin ? 'submitted-form-styled' : 'h-100 d-flex flex-column'}>
            {isAdmin && (
                <div className="d-flex justify-content-between mb-4">
                    <NavigationBar mainTabName="Prompt Templates Library" />
                    <WelcomeUser />
                </div>
            )}

            <Fragment>
                <PromptTemplateFilters
                    onSearch={onSearch}
                    isActive={isActive}
                    setIsActive={() => {
                        setIsActive(!isActive);
                        setCurrentPage(1);
                    }}
                    fetchOnAction={fetchOnAction}
                    disabled={loading}
                    inputRef={inputRef}
                    onSearchTypeChange={onSearchTypeChange}
                    searchType={searchType}
                />

                {isAdmin ? (
                    <Row>
                        <Col>
                            <div className="custom-card card-table d-flex flex-column justify-content-between">
                                <PromptTemplateTableContainer
                                    loading={loading}
                                    data={data}
                                    currentPage={currentPage}
                                    onPageChanged={onPageChanged}
                                    handleUpdatePromptTemplate={handleUpdatePromptTemplate}
                                />
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <PromptTemplateTableContainer
                        loading={loading}
                        handleUpdatePromptTemplate={handleUpdatePromptTemplate}
                        fetchOnAction={fetchOnAction}
                        data={data}
                        currentPage={currentPage}
                        onPageChanged={onPageChanged}
                    />
                )}
            </Fragment>
        </div>
    );
};
